import { useEffect, useState } from 'react';
import { AuthService } from '../services/authService';
import { systemSetupService } from '../services/systemSetupService';

/**
 * «Maks antall per linje» (SystemSetup.MaxLineItemQuantity).
 *
 * Øvre grense for antall en operatør kan registrere på en linje. Brukes av
 * NumericInput (prop `quantity`) som felles sperre mot åpenbare antallsfeil, f.eks.
 * at en EAN skannes inn i antallsfeltet.
 *
 * Verdien hentes én gang per tenant/selskap og deles av alle felter (modul-cache).
 * null = ingen grense (ikke satt, 0, ikke innlogget eller oppslaget feilet).
 */

let cacheKey: string | null = null;
let cachedValue: number | null = null;
let pending: Promise<number | null> | null = null;

const currentKey = (): string | null => {
  if (!AuthService.isAuthenticated()) return null;
  const user = AuthService.getUserDataFromToken();
  return user ? `${user.customerNo}|${user.companyNo}` : null;
};

const load = (key: string): Promise<number | null> => {
  if (cacheKey === key && pending) return pending;
  cacheKey = key;
  cachedValue = null;
  pending = systemSetupService
    .getCurrent()
    .then(setup => {
      const max = setup.maxLineItemQuantity;
      const value = max != null && max > 0 ? max : null;
      if (cacheKey === key) cachedValue = value;
      return value;
    })
    .catch(err => {
      console.warn('Kunne ikke hente MaxLineItemQuantity fra systeminnstillinger:', err);
      if (cacheKey === key) pending = null; // prøv igjen ved neste felt
      return null;
    });
  return pending;
};

/** Kalles når Systeminnstillinger lagres, så nye verdier slår inn uten ny innlogging. */
export const setCachedMaxLineItemQuantity = (max: number | null | undefined): void => {
  const key = currentKey();
  if (!key) return;
  cacheKey = key;
  cachedValue = max != null && max > 0 ? max : null;
  pending = Promise.resolve(cachedValue);
};

/**
 * @param enabled false = ikke hent/ingen grense (felt som ikke er antallsfelt).
 */
export function useMaxLineItemQuantity(enabled: boolean = true): number | null {
  const key = enabled ? currentKey() : null;
  const [max, setMax] = useState<number | null>(key && cacheKey === key ? cachedValue : null);

  useEffect(() => {
    if (!key) {
      setMax(null);
      return;
    }
    let cancelled = false;
    load(key).then(value => {
      if (!cancelled) setMax(value);
    });
    return () => { cancelled = true; };
  }, [key]);

  return max;
}
